import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { BookOpen, Mail, Lock, User, Eye, EyeOff, Sparkles, Crown } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if coming from lifetime offer
  const isLifetimeOffer = searchParams.get('offer') === 'lifetime';

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.email, formData.password, formData.fullName);
      if (result.success) {
        // Track Microsoft Ads conversion for sign-up
        if (typeof window !== 'undefined' && window.uetq) {
          window.uetq.push('event', 'signup', {});
        }
        
        if (isLifetimeOffer) {
          toast.success('Account created! Please check your email to verify, then claim your lifetime access.');
          navigate('/login', { 
            state: { 
              message: 'Please check your email and verify your account, then claim your lifetime access.',
              lifetimeOffer: true
            } 
          });
        } else {
          toast.success('Account created! Please check your email to verify your account before logging in.');
          navigate('/login', { 
            state: { 
              message: 'Please check your email and verify your account before logging in.' 
            } 
          });
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      // Check if Google API is loaded
      if (!window.google || !window.google.accounts) {
        toast.error('Google Sign-In is not available. Please refresh the page.');
        setLoading(false);
        return;
      }

      // Use the simpler popup approach instead of One Tap
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
        scope: 'email profile openid',
        callback: handleGoogleResponse,
      });

      // Request the token
      client.requestAccessToken();
      
    } catch (error) {
      console.error('Google Sign-In initialization error:', error);
      toast.error('Google Sign-In failed. Please try again.');
      setLoading(false);
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      if (response.error) {
        console.error('Google OAuth error:', response.error);
        toast.error('Google sign-up was cancelled or failed');
        setLoading(false);
        return;
      }

      // Get user info using the access token
      const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`);
      const userInfo = await userInfoResponse.json();

      // Create a mock ID token with user info for our backend
      const mockIdToken = btoa(JSON.stringify({
        sub: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
        email_verified: userInfo.verified_email
      }));

      const result = await loginWithGoogle(mockIdToken);

      if (result.success) {
        // Track Microsoft Ads conversion for Google sign-up
        if (typeof window !== 'undefined' && window.uetq) {
          window.uetq.push('event', 'signup', {});
        }
        
        if (isLifetimeOffer) {
          toast.success('Welcome! Account created successfully. Claiming your lifetime access...');
          // For Google users, they're auto-verified, so redirect directly to purchase
          setTimeout(() => {
            window.location.href = '/dashboard?purchase=lifetime';
          }, 1500);
        } else {
          toast.success('Welcome! Your account has been created successfully.');
          navigate('/dashboard');
        }
      } else {
        toast.error(result.error || 'Google sign-up failed');
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      toast.error('Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Start Your Free Trial - Create AI Books Fast"
        description="Join AI BookMoney for free. Get 40 credits and 14 days to create 2-4 complete books. No credit card required. Start earning from AI-assisted publishing today."
        keywords="free trial, AI book generator, KDP publishing trial, start publishing, book creation trial, no credit card"
        canonical="/register"
        ogTitle="Start Your Free AI Book Publishing Trial - 40 Credits Free"
        ogDescription="Join thousands earning from AI books. Free trial includes 40 credits for 2-4 complete books. No credit card required."
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/register"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Register - Start Free Trial",
          "description": "Sign up for AI BookMoney free trial with 40 credits",
          "url": "https://aibookmoney.com/register"
        }}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
            <img src="/logo.svg" alt="AI BookMoney" className="h-12 w-auto" />
            <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              AI BookMoney
            </span>
          </Link>
          {isLifetimeOffer ? (
            <>
              <h2 className="text-3xl font-bold text-white">
                Claim Your Lifetime Access
              </h2>
              <p className="mt-2 text-gray-300">
                Create account → Get $59 lifetime membership
              </p>
              <div className="mt-4 bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                <p className="text-orange-300 text-sm font-medium text-center flex items-center justify-center">
                  <Crown className="h-4 w-4 mr-2" />
                  Limited Time: Only $59 for lifetime access - Only 72 spots left!
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white">
                Start Your Free Trial
              </h2>
              <p className="mt-2 text-gray-300">
                40 credits • 14 days • No credit card required
              </p>
              <div className="mt-4 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-green-300 text-sm font-medium text-center flex items-center justify-center">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Create 2-4 complete books with your free trial
                </p>
              </div>
            </>
          )}
        </div>

        {/* Form */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
          {/* Google Sign-In Button */}
          <div className="mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-gray-800 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-semibold border border-gray-600 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{isLifetimeOffer ? 'Claim with Google' : 'Continue with Google'}</span>
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-900 text-gray-400">or</span>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Full Name Field */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded bg-gray-800"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-300">
                I agree to the{' '}
                <Link to="/terms" className="text-purple-400 hover:text-purple-300">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-purple-400 hover:text-purple-300">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating account...
                </div>
              ) : isLifetimeOffer ? (
                'Claim Lifetime Access'
              ) : (
                'Start Free Trial'
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Conditional Info Box */}
          {isLifetimeOffer ? (
            <div className="mt-4 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <h4 className="text-sm font-semibold text-orange-300 mb-2">Your Lifetime Membership Includes:</h4>
              <ul className="text-xs text-orange-200 space-y-1">
                <li>• 50 credits every month (no rollover)</li>
                <li>• Never pay again - one-time $59 payment</li>
                <li>• All KDP Publishing Pro tools forever</li>
                <li>• Professional cover generator</li>
                <li>• AI metadata optimization</li>
                <li>• Multi-format exports (PDF, EPUB, DOCX)</li>
                <li>• Priority support</li>
              </ul>
            </div>
          ) : (
            <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <h4 className="text-sm font-semibold text-green-300 mb-2">Your Free Trial Includes:</h4>
              <ul className="text-xs text-green-200 space-y-1">
                <li>• 40 credits (enough for 2-4 complete books)</li>
                <li>• All KDP Publishing Pro tools</li>
                <li>• Professional cover generator</li>
                <li>• AI metadata optimization</li>
                <li>• Multi-format exports (PDF, EPUB, DOCX)</li>
                <li>• 14 days to explore everything</li>
              </ul>
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link 
            to="/" 
            className="text-gray-400 hover:text-purple-400 transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;